import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEVICE_INFO_API_URL } from '../../../config/env';

// ─── Fair Usage Types & Constants ──────────────────────────────────────────

interface IFairUsageResponse {
    config: {
        callsLimitPerAgent: number;
        minutesLimitPerAgent: number;
    };
    currentUsage: {
        calls: number;
        minutes: number;
        callsLimit: number;
        minutesLimit: number;
        callsPercentage: number;
        minutesPercentage: number;
        callsRemaining: number;
        minutesRemaining: number;
    };
    monthEnd: string;
}

const FAIR_USAGE_URL = 'https://ai-service.dev.hotelworld.ai/spa-voice-agent/fair-usage/capacity/test?sub=921544e4-20c1-703b-c1d4-62f60c91cf49&assistantId=7ccb5e95-5f20-4a97-9e6c-5be584d43797';
const FAIR_USAGE_BEARER_TOKEN = 'eyJraWQiOiIxQmRuQ1N2MFYrSXJtZklcL1U0a00ybk5iTHMydzNENHZcL0o3dUI5N0dYXC9FPSIsImFsZyI6IlJTMjU2In0.eyJjdXN0b206Y291bnRyeSI6IkdCIiwic3ViIjoiNTI3NTY0MjQtMDAzMS03MDRhLTJjNjgtM2I2ZWIyNDVlNWNjIiwiZW1haWxfdmVyaWZpZWQiOmZhbHNlLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAuZXUtd2VzdC0xLmFtYXpvbmF3cy5jb21cL2V1LXdlc3QtMV9Gdk5veXh3Qm4iLCJjb2duaXRvOnVzZXJuYW1lIjoiZ2xlbl9pdnkiLCJvcmlnaW5fanRpIjoiMGY1NjNhNmEtZWQ2My00YzFlLTg1OTAtZGE3Mjc1ZGEzOTc1IiwiYXVkIjoiNG8wYTVxa3BpaHN1MHZydWdudjN1ZW5rYjEiLCJldmVudF9pZCI6IjMzMWZkNTM0LTQ1OWUtNDBiMy1hY2ZlLTAxM2E4ZTZhZWE5OSIsInRva2VuX3VzZSI6ImlkIiwiYXV0aF90aW1lIjoxNzc1MDQ1MTI3LCJuYW1lIjoiR2xlbiBJdnkiLCJleHAiOjE3NzUwNTIzMjgsImlhdCI6MTc3NTA0ODcyOCwianRpIjoiNmYxMzc3ZjUtNTYxNS00MDUzLTk0YmMtY2U1ZTAzNjAwYzk0IiwiZW1haWwiOiJ0YXVsYW50LmJ5dHlxaSs4ODFAd2luZ3BheS5hcHAifQ.gqH9LS4O-xrMB_YTY7H1dpJtBHztKR9u0fkHYh2NaKmkcjVA2Z_QToXm_9wO5XrwfSJkKvR-HSXWce4NsX4yTYmKIE3I91CPxzFEOF5b14cwXf22QPY1D-Y60r54KeNxe_UH_n1v_fQpOfhXF606ryii57CpaeH1Zl28x9UQbHv4stf3z9FxmnBobNfT46NIXlzfWAJbU6VwpNvkV8OoSqfa2nS9TkOeH1Os5looWTpPN9ATc7QccQqX_OLAumoEbRq_E16lMtmJI6YCvk-osDOyW5N85Jrvc5G2RaKENYxSCyhYBSJlUzN3NuzXd_BQEVyIaEZTyYf26fwCFMYMzw';

const formatOrdinal = (day: number) => {
    const moduloTen = day % 10;
    const moduloHundred = day % 100;

    if (moduloTen === 1 && moduloHundred !== 11) return `${day}st`;
    if (moduloTen === 2 && moduloHundred !== 12) return `${day}nd`;
    if (moduloTen === 3 && moduloHundred !== 13) return `${day}rd`;
    return `${day}th`;
};

const getResetLabel = (monthEnd?: string) => {
    if (!monthEnd) return 'Resets at 00:00';
    const resetDate = new Date(monthEnd);
    const day = formatOrdinal(resetDate.getUTCDate());
    const month = resetDate.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
    return `Resets at 00:00 on the ${day} ${month}`;
};

const buildProgressWidth = (percentage: number) => {
    const clampedPercentage = Math.max(0, Math.min(100, percentage || 0));
    return `${clampedPercentage}%`;
};

const UsageCard = ({
    title,
    used,
    limit,
    usedPercentage,
    remaining,
}: {
    title: string;
    used: number;
    limit: number;
    usedPercentage: number;
    remaining: number;
}) => {
    return (
        <div className='rounded-xl border border-[#0B4D23] p-2.5 md:p-3'>
            <div className='flex items-center gap-1.5 mb-1.5'>
                <p className='text-base md:text-[17px] leading-tight font-semibold text-[#111827] dark:text-primary-darkMode'>
                    {title}
                </p>
                <div className='w-4 h-4 rounded-full border border-[#0B4D23] text-[#0B4D23] text-[9px] font-bold flex items-center justify-center'>
                    i
                </div>
            </div>

            <p className='text-[26px] md:text-[40px] leading-none text-[#00330F] font-medium'>
                {used}/{limit}
            </p>

            <div className='mt-3 mb-1.5 w-full h-1.5 rounded-full bg-[#D1D5DB] overflow-hidden'>
                <div
                    className='h-full rounded-full bg-[#0EA15B]'
                    style={{ width: buildProgressWidth(usedPercentage) }}
                />
            </div>

            <div className='flex items-center gap-4 text-sm md:text-[14px] text-[#6B7280]'>
                <p>{usedPercentage.toFixed(1)}% Used</p>
                <p>{remaining} Remaining</p>
            </div>
        </div>
    );
};

// ─── Vapi KB Upload Types & Constants ──────────────────────────────────────

const VAPI_TOOL_ID = 'acb36700-93b3-4993-9b07-81704a510170';
const HOTEL_SUB = '52756424-0031-704a-2c68-3b6eb245e5cc';
const BACKEND_URL = DEVICE_INFO_API_URL as string;
const ACCEPTED_FILE_TYPES = '.pdf,.txt,.doc,.docx,.csv';

interface KBFile {
    vapiFileId: string;
    name: string;
    size: number;
    status: string;
    createdAt: string;
}

interface Issue {
    id: string;
    type: 'contradiction' | 'terminology';
    category: 'Contradiction' | 'Acronym' | 'HotelTerminology' | 'UnfamiliarTerm' | 'Synonym';
    summary: string;
    existingFile?: string;
    existingStatement?: string;
    newStatement?: string;
}

interface Draft {
    _id: string;
    fileName: string;
    status: string;
    issues: Issue[];
    createdAt: string;
}

type ActiveView = null | 'kbFiles' | 'drafts' | 'issuesReview';

interface Notification {
    type: 'success' | 'error';
    message: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

const CATEGORY_STYLES: Record<string, { bg: string; text: string; ring: string }> = {
    Contradiction: { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' },
    Acronym: { bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-200' },
    HotelTerminology: { bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-200' },
    UnfamiliarTerm: { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
    Synonym: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
};

function CategoryBadge({ category }: { category: string }) {
    const s = CATEGORY_STYLES[category] ?? { bg: 'bg-zinc-50', text: 'text-zinc-700', ring: 'ring-zinc-200' };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${s.bg} ${s.text} ${s.ring}`}>
            {category}
        </span>
    );
}

function resolutionPlaceholder(category: string): string {
    switch (category) {
        case 'Contradiction': return 'e.g. Updated operating hours effective Q3 2026. New policy replaces previous schedule.';
        case 'Acronym': return 'e.g. CCM = Capacity Charging Management';
        case 'HotelTerminology': return 'e.g. Bathhouse = Indoor spa facility with steam rooms and therapeutic baths';
        case 'UnfamiliarTerm': return 'e.g. Temescal Valley = City/region in California where resort is located';
        case 'Synonym': return 'e.g. These terms are used interchangeably to mean the same thing.';
        default: return 'Provide context or explanation for this issue...';
    }
}

// ─── VapiKbUpload Component ─────────────────────────────────────────────────

const VapiKbUpload: React.FC = () => {
    const [activeView, setActiveView] = useState<ActiveView>(null);

    // KB files
    const [kbFiles, setKbFiles] = useState<KBFile[]>([]);
    const [loadingKbFiles, setLoadingKbFiles] = useState(false);
    const [kbFilesError, setKbFilesError] = useState('');

    // File preview
    const [previewFileId, setPreviewFileId] = useState<string | null>(null);
    const [previewFileName, setPreviewFileName] = useState('');
    const [previewContent, setPreviewContent] = useState('');
    const [previewTruncated, setPreviewTruncated] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);

    // File analysis
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [analyzing, setAnalyzing] = useState(false);
    const [analyzeError, setAnalyzeError] = useState('');

    // Issues review
    const [draftId, setDraftId] = useState('');
    const [issues, setIssues] = useState<Issue[]>([]);
    const [resolutions, setResolutions] = useState<Record<string, string>>({});

    // Upload
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    // Pending drafts
    const [pendingDrafts, setPendingDrafts] = useState<Draft[]>([]);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [draftsError, setDraftsError] = useState('');

    // Notification
    const [notification, setNotification] = useState<Notification | null>(null);
    const notifTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Load saved resolutions when draftId changes
    useEffect(() => {
        if (!draftId) return;
        try {
            const saved = localStorage.getItem(`vapi_kb_res_${draftId}`);
            if (saved) setResolutions(JSON.parse(saved));
        } catch {}
    }, [draftId]);

    // Persist resolutions to localStorage
    useEffect(() => {
        if (!draftId) return;
        localStorage.setItem(`vapi_kb_res_${draftId}`, JSON.stringify(resolutions));
    }, [draftId, resolutions]);

    useEffect(() => () => {
        if (notifTimer.current) clearTimeout(notifTimer.current);
    }, []);

    const showNotification = useCallback((type: 'success' | 'error', message: string) => {
        setNotification({ type, message });
        if (notifTimer.current) clearTimeout(notifTimer.current);
        notifTimer.current = setTimeout(() => setNotification(null), 6000);
    }, []);

    function buildHeaders(includeJson = false): HeadersInit {
        const h: Record<string, string> = {
            Authorization: `Bearer ${FAIR_USAGE_BEARER_TOKEN}`,
        };
        if (includeJson) h['Content-Type'] = 'application/json';
        return h;
    }

    async function fetchKbFiles() {
        setLoadingKbFiles(true);
        setKbFilesError('');
        setActiveView('kbFiles');
        setPreviewFileId(null);
        try {
            const res = await fetch(
                `${BACKEND_URL}/vapi/knowledge-base/tool-files?toolId=${VAPI_TOOL_ID}`,
                { headers: buildHeaders() },
            );
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
            const data = await res.json();
            setKbFiles(data.files || []);
        } catch (err: any) {
            setKbFilesError(err?.message || 'Failed to load KB files');
        } finally {
            setLoadingKbFiles(false);
        }
    }

    async function fetchFilePreview(fileId: string, fileName: string) {
        setPreviewFileId(fileId);
        setPreviewFileName(fileName);
        setPreviewContent('');
        setPreviewTruncated(false);
        setLoadingPreview(true);
        try {
            const res = await fetch(
                `${BACKEND_URL}/vapi/knowledge-base/tool-file-preview?toolId=${VAPI_TOOL_ID}&fileId=${fileId}&maxChars=6000`,
                { headers: buildHeaders() },
            );
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
            const data = await res.json();
            setPreviewContent(data.preview || '');
            setPreviewTruncated(!!data.truncated);
        } catch (err: any) {
            setPreviewContent(`Error: ${err?.message || 'Unknown error'}`);
        } finally {
            setLoadingPreview(false);
        }
    }

    async function analyzeFile() {
        if (!selectedFile) {
            showNotification('error', 'Please select a file first.');
            return;
        }

        setAnalyzing(true);
        setAnalyzeError('');
        setActiveView(null);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const res = await fetch(
                `${BACKEND_URL}/vapi/knowledge-base/analyze-tool?toolId=${VAPI_TOOL_ID}&hotelSub=${HOTEL_SUB}`,
                { method: 'POST', headers: buildHeaders(), body: formData },
            );
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData?.message || `${res.status} ${res.statusText}`);
            }
            const data = await res.json();

            if (data.blocked) {
                setDraftId(data.draftId || '');
                setIssues(data.issues || []);
                setResolutions({});
                setActiveView('issuesReview');
            } else {
                const fileId = data.vapiFileId ? ` (ID: ${data.vapiFileId})` : '';
                showNotification('success', `File uploaded successfully!${fileId}`);
                setSelectedFile(null);
                setFileInputKey(k => k + 1);
            }
        } catch (err: any) {
            const msg = err?.message || 'Analysis failed';
            setAnalyzeError(msg);
            showNotification('error', msg);
        } finally {
            setAnalyzing(false);
        }
    }

    async function uploadWithResolutions() {
        const unresolved = issues.filter(i => !resolutions[i.id]?.trim());
        if (unresolved.length > 0) {
            showNotification('error', `Please resolve all ${unresolved.length} remaining issue(s) before uploading.`);
            return;
        }

        setUploading(true);
        setUploadError('');

        const resolutionList = issues.map(i => ({
            issueId: i.id,
            resolution: resolutions[i.id].trim(),
        }));

        try {
            const res = await fetch(`${BACKEND_URL}/vapi/knowledge-base/resolve-upload`, {
                method: 'POST',
                headers: buildHeaders(true),
                body: JSON.stringify({ hotelSub: HOTEL_SUB, draftId, resolutions: resolutionList }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData?.message || `${res.status} ${res.statusText}`);
            }
            const data = await res.json();
            localStorage.removeItem(`vapi_kb_res_${draftId}`);
            showNotification('success', data.message || 'File uploaded successfully!');
            setActiveView(null);
            setSelectedFile(null);
            setFileInputKey(k => k + 1);
            setDraftId('');
            setIssues([]);
            setResolutions({});
        } catch (err: any) {
            const msg = err?.message || 'Upload failed';
            setUploadError(msg);
            showNotification('error', msg);
        } finally {
            setUploading(false);
        }
    }

    function discardDraft() {
        localStorage.removeItem(`vapi_kb_res_${draftId}`);
        setDraftId('');
        setIssues([]);
        setResolutions({});
        setActiveView(null);
        setSelectedFile(null);
        setFileInputKey(k => k + 1);
        showNotification('success', 'Draft discarded.');
    }

    async function fetchPendingDrafts() {
        setLoadingDrafts(true);
        setDraftsError('');
        setActiveView('drafts');
        try {
            const res = await fetch(
                `${BACKEND_URL}/vapi/knowledge-base/drafts?hotelSub=${HOTEL_SUB}`,
                { headers: buildHeaders() },
            );
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
            const data = await res.json();
            setPendingDrafts(data.drafts || []);
        } catch (err: any) {
            setDraftsError(err?.message || 'Failed to load drafts');
        } finally {
            setLoadingDrafts(false);
        }
    }

    function resumeDraft(draft: Draft) {
        setDraftId(draft._id);
        setIssues(draft.issues || []);
        try {
            const saved = localStorage.getItem(`vapi_kb_res_${draft._id}`);
            setResolutions(saved ? JSON.parse(saved) : {});
        } catch {
            setResolutions({});
        }
        setActiveView('issuesReview');
    }

    const resolvedCount = issues.filter(i => resolutions[i.id]?.trim()).length;
    const allResolved = issues.length > 0 && resolvedCount === issues.length;
    const progressPct = issues.length > 0 ? Math.round((resolvedCount / issues.length) * 100) : 0;

    return (
        <div className='mt-8'>
            {/* Section header */}
            <div className='mb-4'>
                <h2 className='text-lg font-semibold text-[#111827] dark:text-primary-darkMode'>
                    Knowledge Base Upload
                </h2>
                <p className='text-sm text-[#6B7280] dark:text-gray-400 mt-0.5'>
                    Upload files to Claire&apos;s Vapi knowledge base. Contradictions and terminology issues are flagged for review before upload.
                </p>
            </div>

            {/* Notification */}
            {notification && (
                <div
                    className={`mb-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${
                        notification.type === 'success'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-red-200 bg-red-50 text-red-800'
                    }`}
                >
                    <span>{notification.message}</span>
                    <button
                        onClick={() => setNotification(null)}
                        className='shrink-0 text-xs opacity-60 hover:opacity-100'
                        aria-label='Dismiss notification'
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Actions */}
            <div className='flex flex-wrap gap-2 mb-4'>
                <button
                    onClick={fetchKbFiles}
                    disabled={loadingKbFiles}
                    className='rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                    {loadingKbFiles ? 'Loading...' : 'View current KB files'}
                </button>
                <button
                    onClick={fetchPendingDrafts}
                    disabled={loadingDrafts}
                    className='rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                    {loadingDrafts ? 'Loading...' : 'View pending drafts'}
                </button>
            </div>

            {/* File Upload — hidden when reviewing issues */}
            {activeView !== 'issuesReview' && (
                <div className='rounded-xl border border-zinc-200 bg-white shadow-sm p-4 mb-4'>
                    <div className='text-sm font-semibold text-zinc-900 mb-3'>Analyze & Upload File</div>
                    <div className='flex flex-col sm:flex-row gap-3'>
                        <div className='flex-1 min-w-0'>
                            <input
                                key={fileInputKey}
                                type='file'
                                accept={ACCEPTED_FILE_TYPES}
                                onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                                className='block w-full text-sm text-zinc-600 file:mr-3 file:rounded-lg file:border-0 file:bg-zinc-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-zinc-700 hover:file:bg-zinc-200 cursor-pointer'
                            />
                            <p className='mt-1 text-[11px] text-zinc-400'>PDF, TXT, DOC, DOCX, CSV</p>
                        </div>
                        <button
                            onClick={analyzeFile}
                            disabled={analyzing || !selectedFile}
                            className='shrink-0 flex items-center justify-center gap-2 rounded-lg bg-[#0B4D23] px-5 py-2 text-sm font-semibold text-white hover:bg-[#083818] disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {analyzing ? (
                                <>
                                    <svg className='h-4 w-4 animate-spin' viewBox='0 0 24 24' fill='none'>
                                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                                    </svg>
                                    Analyzing...
                                </>
                            ) : 'Analyze & Upload'}
                        </button>
                    </div>
                    {analyzing && (
                        <p className='mt-2 text-xs text-zinc-500'>
                            Analysis may take 30–60 seconds. Please wait...
                        </p>
                    )}
                    {analyzeError && (
                        <p className='mt-2 text-xs text-red-600'>{analyzeError}</p>
                    )}
                </div>
            )}

            {/* KB Files */}
            {activeView === 'kbFiles' && (
                <div className='rounded-xl border border-zinc-200 bg-white shadow-sm mb-4'>
                    <div className='flex items-center justify-between border-b border-zinc-100 px-4 py-3'>
                        <div className='text-sm font-semibold text-zinc-900'>
                            Current KB Files
                            {kbFiles.length > 0 && (
                                <span className='ml-2 text-xs font-normal text-zinc-400'>({kbFiles.length})</span>
                            )}
                        </div>
                        <button
                            onClick={() => { setActiveView(null); setPreviewFileId(null); }}
                            className='text-xs text-zinc-500 hover:text-zinc-700'
                        >
                            Close
                        </button>
                    </div>

                    {kbFilesError ? (
                        <div className='p-4 text-sm text-red-600'>{kbFilesError}</div>
                    ) : kbFiles.length === 0 ? (
                        <div className='p-4 text-sm text-zinc-500'>No files found in knowledge base.</div>
                    ) : (
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-zinc-100 bg-zinc-50'>
                                        <th className='px-4 py-2.5 text-left text-xs font-semibold text-zinc-600'>Name</th>
                                        <th className='px-4 py-2.5 text-left text-xs font-semibold text-zinc-600'>Size</th>
                                        <th className='px-4 py-2.5 text-left text-xs font-semibold text-zinc-600'>Status</th>
                                        <th className='px-4 py-2.5 text-left text-xs font-semibold text-zinc-600'>Uploaded</th>
                                        <th className='px-4 py-2.5 text-left text-xs font-semibold text-zinc-600'>Vapi File ID</th>
                                        <th className='px-4 py-2.5' />
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-zinc-100'>
                                    {kbFiles.map(f => (
                                        <React.Fragment key={f.vapiFileId}>
                                            <tr className='hover:bg-zinc-50'>
                                                <td className='px-4 py-3 font-medium text-zinc-900 max-w-[180px] truncate'>{f.name}</td>
                                                <td className='px-4 py-3 text-zinc-500 whitespace-nowrap'>{formatFileSize(f.size)}</td>
                                                <td className='px-4 py-3'>
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${
                                                        f.status === 'indexed'
                                                            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                                                            : 'bg-zinc-50 text-zinc-600 ring-zinc-200'
                                                    }`}>
                                                        {f.status}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3 text-zinc-500 whitespace-nowrap'>{formatDate(f.createdAt)}</td>
                                                <td className='px-4 py-3 font-mono text-xs text-zinc-400 max-w-[140px] truncate'>{f.vapiFileId}</td>
                                                <td className='px-4 py-3'>
                                                    {previewFileId === f.vapiFileId ? (
                                                        <button
                                                            onClick={() => setPreviewFileId(null)}
                                                            className='text-xs text-zinc-500 hover:text-zinc-700'
                                                        >
                                                            Hide
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => fetchFilePreview(f.vapiFileId, f.name)}
                                                            className='text-xs font-semibold text-[#0B4D23] hover:underline'
                                                        >
                                                            Read
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {previewFileId === f.vapiFileId && (
                                                <tr>
                                                    <td colSpan={6} className='px-4 pb-4 pt-0'>
                                                        <div className='rounded-lg border border-zinc-200 bg-zinc-50 p-3'>
                                                            <div className='flex items-center justify-between mb-2'>
                                                                <span className='text-xs font-semibold text-zinc-700'>
                                                                    Preview: {previewFileName}
                                                                </span>
                                                                <button
                                                                    onClick={() => setPreviewFileId(null)}
                                                                    className='text-xs text-zinc-500 hover:text-zinc-700'
                                                                >
                                                                    Close
                                                                </button>
                                                            </div>
                                                            {loadingPreview ? (
                                                                <p className='text-xs text-zinc-500'>Loading preview...</p>
                                                            ) : (
                                                                <>
                                                                    <pre className='max-h-64 overflow-auto text-xs text-zinc-700 whitespace-pre-wrap'>
                                                                        {previewContent}
                                                                    </pre>
                                                                    {previewTruncated && (
                                                                        <p className='mt-2 text-[11px] text-zinc-400'>
                                                                            Preview truncated to first 6,000 characters.
                                                                        </p>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Pending Drafts */}
            {activeView === 'drafts' && (
                <div className='rounded-xl border border-zinc-200 bg-white shadow-sm mb-4'>
                    <div className='flex items-center justify-between border-b border-zinc-100 px-4 py-3'>
                        <div className='text-sm font-semibold text-zinc-900'>Pending Drafts</div>
                        <button
                            onClick={() => setActiveView(null)}
                            className='text-xs text-zinc-500 hover:text-zinc-700'
                        >
                            Close
                        </button>
                    </div>
                    {draftsError ? (
                        <div className='p-4 text-sm text-red-600'>{draftsError}</div>
                    ) : pendingDrafts.length === 0 ? (
                        <div className='p-4 text-sm text-zinc-500'>No pending drafts found.</div>
                    ) : (
                        <ul className='divide-y divide-zinc-100'>
                            {pendingDrafts.map(d => (
                                <li key={d._id} className='flex items-center justify-between px-4 py-3 hover:bg-zinc-50'>
                                    <div className='min-w-0 mr-4'>
                                        <div className='text-sm font-medium text-zinc-900 truncate'>{d.fileName}</div>
                                        <div className='text-xs text-zinc-500 mt-0.5'>
                                            {d.issues?.length ?? 0} issue(s) &bull; {formatDate(d.createdAt)}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => resumeDraft(d)}
                                        className='shrink-0 rounded-lg bg-[#0B4D23] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#083818]'
                                    >
                                        Review
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Issues Review */}
            {activeView === 'issuesReview' && (
                <div className='rounded-xl border border-zinc-200 bg-white shadow-sm mb-4'>
                    {/* Header */}
                    <div className='flex flex-col gap-3 border-b border-zinc-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between'>
                        <div>
                            <div className='text-sm font-semibold text-zinc-900'>Review Issues</div>
                            <div className='text-xs text-zinc-500 mt-0.5'>
                                Resolve all issues before uploading.
                                {draftId && (
                                    <span className='ml-1'>Draft: <span className='font-mono'>{draftId}</span></span>
                                )}
                            </div>
                        </div>
                        <div className='flex shrink-0 gap-2'>
                            <button
                                onClick={discardDraft}
                                className='rounded-lg border border-zinc-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50'
                            >
                                Discard Draft
                            </button>
                            <button
                                onClick={uploadWithResolutions}
                                disabled={!allResolved || uploading}
                                className='rounded-lg bg-[#0B4D23] px-3 py-2 text-xs font-semibold text-white hover:bg-[#083818] disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                {uploading ? 'Uploading...' : 'Upload with Resolutions'}
                            </button>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className='px-4 pt-3 pb-2'>
                        <div className='flex items-center justify-between text-xs text-zinc-600 mb-1.5'>
                            <span>{resolvedCount} of {issues.length} issues resolved</span>
                            <span>{progressPct}%</span>
                        </div>
                        <div className='h-2 w-full rounded-full bg-zinc-100 overflow-hidden'>
                            <div
                                className='h-full rounded-full bg-[#0EA15B] transition-all duration-300'
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                    </div>

                    {uploadError && (
                        <div className='mx-4 mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'>
                            {uploadError}
                        </div>
                    )}

                    {/* Issues list */}
                    <div className='divide-y divide-zinc-100'>
                        {issues.map((issue, idx) => {
                            const resolution = resolutions[issue.id] || '';
                            const isResolved = resolution.trim().length > 0;
                            return (
                                <div
                                    key={issue.id}
                                    className={`p-4 transition-colors ${isResolved ? 'bg-emerald-50/40' : ''}`}
                                >
                                    <div className='flex flex-wrap items-center gap-2 mb-2'>
                                        <span className='text-xs font-semibold text-zinc-400'>#{idx + 1}</span>
                                        <CategoryBadge category={issue.category} />
                                        {isResolved && (
                                            <span className='inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200'>
                                                ✓ Resolved
                                            </span>
                                        )}
                                    </div>

                                    <p className='text-sm text-zinc-800 mb-3 leading-relaxed'>{issue.summary}</p>

                                    {(issue.existingStatement || issue.existingFile) && (
                                        <div className='mb-2'>
                                            <div className='text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1'>
                                                Existing KB{issue.existingFile ? ` — ${issue.existingFile}` : ''}
                                            </div>
                                            <div className='rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700 leading-relaxed'>
                                                {issue.existingStatement || '—'}
                                            </div>
                                        </div>
                                    )}

                                    {issue.newStatement && (
                                        <div className='mb-3'>
                                            <div className='text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1'>
                                                New File
                                            </div>
                                            <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 leading-relaxed'>
                                                {issue.newStatement}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className='text-xs font-semibold text-zinc-700'>
                                            Your Resolution <span className='text-red-500'>*</span>
                                        </label>
                                        <textarea
                                            value={resolution}
                                            onChange={e =>
                                                setResolutions(prev => ({ ...prev, [issue.id]: e.target.value }))
                                            }
                                            rows={3}
                                            placeholder={resolutionPlaceholder(issue.category)}
                                            className='mt-1.5 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-300 resize-none'
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className='flex justify-end gap-2 border-t border-zinc-100 px-4 py-3'>
                        <button
                            onClick={discardDraft}
                            className='rounded-lg border border-zinc-200 px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50'
                        >
                            Discard Draft
                        </button>
                        <button
                            onClick={uploadWithResolutions}
                            disabled={!allResolved || uploading}
                            className='rounded-lg bg-[#0B4D23] px-4 py-2 text-xs font-semibold text-white hover:bg-[#083818] disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {uploading
                                ? 'Uploading...'
                                : `Upload with Resolutions (${resolvedCount}/${issues.length})`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Page ─────────────────────────────────────────────────────────────

const ClaireGlenIvy = () => {
    const [data, setData] = useState<IFairUsageResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const getCapacityData = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await fetch(FAIR_USAGE_URL, {
                    method: 'GET',
                    headers: {
                        Accept: '*/*',
                        Authorization: `Bearer ${FAIR_USAGE_BEARER_TOKEN}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                }

                const payload = await response.json();
                setData(payload);
            } catch (err: any) {
                setError(err?.message || 'Failed to fetch fair usage data');
            } finally {
                setLoading(false);
            }
        };

        getCapacityData();
    }, []);

    const usage = data?.currentUsage;
    const callsLimit = usage?.callsLimit ?? data?.config?.callsLimitPerAgent ?? 0;
    const minutesLimit = usage?.minutesLimit ?? data?.config?.minutesLimitPerAgent ?? 0;
    const resetLabel = useMemo(() => getResetLabel(data?.monthEnd), [data?.monthEnd]);

    return (
        <div className='w-full'>
            <div className='mb-3'>
                <h1 className='text-xl font-bold text-[#101010] dark:text-primary-darkMode'>
                    Claire - Glen Ivy
                </h1>
            </div>

            <div className='p-1 md:p-0'>
                <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-1.5 mb-2'>
                    <p className='text-lg md:text-[20px] font-semibold text-[#111827] dark:text-primary-darkMode'>Month to Date Usage</p>
                    <p className='text-sm md:text-[14px] text-[#667085] dark:text-gray-300'>{resetLabel}</p>
                </div>

                {loading && (
                    <div className='text-sm text-gray-600 dark:text-gray-300'>
                        Loading usage data...
                    </div>
                )}

                {!loading && error && (
                    <div className='text-sm text-red-600'>
                        {error}
                    </div>
                )}

                {!loading && !error && usage && (
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-2.5'>
                        <UsageCard
                            title='Calls Month to date'
                            used={usage.calls}
                            limit={callsLimit}
                            usedPercentage={usage.callsPercentage}
                            remaining={usage.callsRemaining}
                        />
                        <UsageCard
                            title='Total Minutes'
                            used={usage.minutes}
                            limit={minutesLimit}
                            usedPercentage={usage.minutesPercentage}
                            remaining={usage.minutesRemaining}
                        />
                    </div>
                )}
            </div>

            <VapiKbUpload />
        </div>
    );
};

export default ClaireGlenIvy;
